import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const UserProfile = () => {
  const { user, logout, sessionId, isGuest } = useAuth();
  const [scores, setScores] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchScores();
  }, [page, user, sessionId]);

  const fetchScores = async () => {
    setLoading(true);
    try {
      let response;
      if (isGuest && sessionId) {
        response = await axios.get(`/api/quiz/scores/guest/${sessionId}?page=${page}&limit=10`);
      } else if (user) {
        response = await axios.get(`/api/auth/scores?page=${page}&limit=10`);
      } else {
        setLoading(false);
        return;
      }

      if (response.data.success) {
        setScores(response.data.data.scores);
        setStats(response.data.data.stats);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching scores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getScoreColor = percentage => {
    if (percentage >= 80) return '#22c55e';
    if (percentage >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const getScoreEmoji = percentage => {
    if (percentage >= 90) return '🏆';
    if (percentage >= 80) return '🎉';
    if (percentage >= 70) return '👏';
    if (percentage >= 60) return '👍';
    return '📚';
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          padding: '2rem 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className='text-center'>
          <div className='loading-spinner' style={{ margin: '0 auto 1rem' }}></div>
          <p style={{ color: 'white', fontSize: '1.2rem' }}>Loading your scores...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', padding: '2rem 0' }}>
      <div className='container'>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '2rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button
              onClick={() => navigate('/')}
              className='btn btn-secondary'
              style={{ marginRight: '1rem' }}
            >
              ← Back
            </button>
            <div>
              <h1 style={{ fontSize: '2rem', color: 'white', margin: 0 }}>
                {isGuest ? 'Guest Profile' : user?.name || 'User Profile'}
              </h1>
              <p style={{ color: 'rgba(255,255,255,0.7)', margin: 0 }}>
                {isGuest ? 'Playing as guest' : user?.email}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            {isGuest ? (
              <a
                href='/api/auth/google'
                className='btn btn-primary'
                style={{ textDecoration: 'none' }}
              >
                Sign in with Google
              </a>
            ) : (
              <button onClick={handleLogout} className='btn btn-secondary'>
                Logout
              </button>
            )}
          </div>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className='grid grid-3 mb-8'>
            <div className='card text-center'>
              <h3 style={{ fontSize: '2rem', color: '#ff8c00', margin: '0 0 0.5rem 0' }}>
                {stats.totalQuizzes}
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0 }}>Total Quizzes</p>
            </div>
            <div className='card text-center'>
              <h3 style={{ fontSize: '2rem', color: '#ff8c00', margin: '0 0 0.5rem 0' }}>
                {Math.round(stats.averageScore)}%
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0 }}>Average Score</p>
            </div>
            <div className='card text-center'>
              <h3 style={{ fontSize: '2rem', color: '#ff8c00', margin: '0 0 0.5rem 0' }}>
                {stats.bestScore}%
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0 }}>Best Score</p>
            </div>
          </div>
        )}

        {/* Recent Scores */}
        <div className='card'>
          <h2 style={{ fontSize: '1.5rem', color: 'white', marginBottom: '1.5rem' }}>
            Recent Quiz Scores
          </h2>

          {scores.length === 0 ? (
            <div className='text-center' style={{ padding: '2rem 0' }}>
              <span style={{ fontSize: '3rem', display: 'block', marginBottom: '1rem' }}>🎯</span>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.1rem' }}>
                No quiz scores yet. Start playing to see your results here!
              </p>
              <button
                onClick={() => navigate('/')}
                className='btn btn-primary'
                style={{ marginTop: '1rem' }}
              >
                Take Your First Quiz
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {scores.map((score, index) => (
                  <div
                    key={score._id || index}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1rem',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      borderRadius: '0.5rem',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '1.1rem', color: 'white', margin: '0 0 0.25rem 0' }}>
                        {score.movieName}
                      </h3>
                      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', margin: 0 }}>
                        {formatDate(score.completedAt)} • {score.language} • {score.quizType}
                      </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem' }}>{getScoreEmoji(score.percentage)}</div>
                        <div
                          style={{
                            fontSize: '1.2rem',
                            fontWeight: 'bold',
                            color: getScoreColor(score.percentage),
                          }}
                        >
                          {score.percentage}%
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
                          {score.score.correct}/{score.score.total}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination && pagination.pages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '1rem',
                    marginTop: '2rem',
                  }}
                >
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className='btn btn-secondary'
                    style={{ opacity: page === 1 ? 0.5 : 1 }}
                  >
                    Previous
                  </button>
                  <span style={{ color: 'white', display: 'flex', alignItems: 'center' }}>
                    Page {page} of {pagination.pages}
                  </span>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === pagination.pages}
                    className='btn btn-secondary'
                    style={{ opacity: page === pagination.pages ? 0.5 : 1 }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
